import { Dispatch } from '@reduxjs/toolkit';
import { Dictionary } from 'lodash';
import { keyBy, uniqBy } from 'lodash/fp';
import { NavigateFunction } from 'react-router-dom-v5-compat';
import { qs } from 'url-parse';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { ALL_TRIGGER_TYPES, Trigger } from 'dashboard/models/trigger';
import { Workflow } from 'dashboard/models/workflow';
import {
  TriggerObject,
  WORKFLOW_RUN_TERMINAL_STATES,
  WorkflowRun,
  WorkflowRunType,
} from 'dashboard/models/workflow-run';
import {
  WorkflowRunSimulateContext,
  WorkflowRunSimulated,
} from 'dashboard/models/workflow-run-simulated';
import { WorkflowRunStep, WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { setWorkflow, setWorkflowVersion } from 'dashboard/redux/actions/workflow';
import {
  setWorkflowRun,
  setWorkflowRunKnownObjects,
  setWorkflowRunLoopChildrenSteps,
  setWorkflowRunSteps,
  setWorkflowRunTriggerObject,
} from 'dashboard/redux/actions/workflow-run';
import { setModuleRunStepsByConfigStepToken } from 'dashboard/redux/slices/workflow-editor';
import { setWorkflowEditorContext } from 'dashboard/redux/thunk-actions/workflow';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiListResponse, ApiModel, ApiResource } from 'lib/interfaces/api';

export const loadWorkflowRun = (
  workflowRunId: string,
  navigate: NavigateFunction,
): DashboardThunkAction => {
  return async (dispatch) => {
    const { data: workflowRun, included = [] } =
      await SessionClientDashboard.getResource<WorkflowRun>(ApiEndpoint.WorkflowRun, workflowRunId);

    if (workflowRun.attributes['run-type'] === WorkflowRunType.Module) {
      const parentRun = included.find(
        (item): item is WorkflowRun => item.type === ApiModel.WorkflowRun,
      );
      if (parentRun) {
        const parentWorkflowId = parentRun.relationships.workflow.data.id;
        navigate(`/dashboard/workflows/${parentWorkflowId}/runs/${parentRun.id}`);
      }
    } else {
      dispatch(setWorkflowRun(workflowRun));
      dispatch(setWorkflowRunIncluded(workflowRun, included));
    }
  };
};

export const loadWorkflowRunLoopChildrenSteps = (
  workflowRunId: string,
  loopConfigStepId: string,
  loopIterationIdx: number,
): DashboardThunkAction => {
  return async (dispatch) => {
    const childrenSteps = (
      await SessionClientDashboard.getResourceMeta<{
        'children-steps': ApiListResponse<WorkflowRunStep>;
      }>(
        ApiEndpoint.WorkflowRun,
        `${workflowRunId}/loop-children?${qs.stringify({
          'loop-config-step-id': loopConfigStepId,
          'loop-iteration-idx': loopIterationIdx,
        })}`,
      )
    )?.meta?.['children-steps'];

    if (childrenSteps) {
      dispatch(
        setWorkflowRunLoopChildrenSteps({
          [loopConfigStepId]: { [loopIterationIdx]: childrenSteps.data },
        }),
      );
    }
  };
};

export const pollForWorkflowRunComplete = (
  workflowRunId: string,
  onComplete?: () => void,
  onTimeout?: () => void,
) => {
  return async (dispatch: Dispatch) => {
    const start = Date.now();
    let pollTimeout = 1000;
    let timeout: NodeJS.Timeout | null = null;

    async function poll() {
      const { data: workflowRun, included } = await SessionClientDashboard.getResource<WorkflowRun>(
        ApiEndpoint.WorkflowRun,
        workflowRunId,
      );
      dispatch(setWorkflowRun(workflowRun));

      const relationshipMap = keyBy('id', included);
      const steps = workflowRun.relationships.steps.data.map(
        ({ id }) => relationshipMap[id],
      ) as WorkflowRunStep[];

      const triggerObjectId = workflowRun.relationships['trigger-object']?.data?.id;
      const allKnownObjects = steps
        .flatMap((step) => step.relationships['known-objects'].data)
        .map((model) => relationshipMap[model.id] as WorkflowRunStepKnownObjects);
      const uniqueKnownObjects = uniqBy('id', allKnownObjects);

      dispatch(setWorkflowRunSteps(steps));
      if (triggerObjectId) {
        dispatch(setWorkflowRunTriggerObject(relationshipMap[triggerObjectId] as TriggerObject));
      }
      dispatch(setWorkflowRunKnownObjects(uniqueKnownObjects));

      if (pollTimeout === 1000 && Date.now() > start + 10000) {
        pollTimeout = 3000;
      }

      if (pollTimeout === 3000 && Date.now() > start + 25000) {
        pollTimeout = 5000;
      }

      if (
        WORKFLOW_RUN_TERMINAL_STATES.includes(workflowRun.attributes.status) ||
        Date.now() > start + 60000
      ) {
        if (WORKFLOW_RUN_TERMINAL_STATES.includes(workflowRun.attributes.status)) {
          if (onComplete) {
            onComplete();
          }
        } else {
          if (onTimeout) {
            onTimeout();
          }
        }
      } else {
        timeout = setTimeout(poll, pollTimeout);
      }
    }

    timeout = setTimeout(poll, pollTimeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  };
};

export const runSimulatedWorkflowRun = (
  workflowRun: WorkflowRunSimulated,
  navigate: NavigateFunction,
  simulateContext?: WorkflowRunSimulateContext,
): DashboardThunkAction => {
  return async (dispatch) => {
    if (simulateContext) {
      const attributes = {
        ...workflowRun.attributes,
        'simulate-context': simulateContext,
      };
      await SessionClientDashboard.updateResource<WorkflowRunSimulated>({
        type: ApiModel.WorkflowRunSimulated,
        id: workflowRun.id,
        attributes,
      });
    }
    await SessionClientDashboard.postResourceAction<WorkflowRunSimulated>(
      {
        id: workflowRun.id,
        type: ApiModel.WorkflowRunSimulated,
      },
      'simulate',
      {
        meta: {},
      },
    );
    dispatch(loadWorkflowRun(workflowRun.id, navigate));
  };
};

export const createSimulatedWorkflowRun = (
  workflowId: Workflow['id'],
  workflowVersionId: WorkflowVersion['id'],
  navigate: NavigateFunction,
  simulateContext?: WorkflowRunSimulateContext,
): DashboardThunkAction => {
  return async (dispatch) => {
    const attributes = {
      'workflow-version-id': workflowVersionId,
      ...(simulateContext ? { 'simulate-context': simulateContext } : {}),
    };
    const { data: workflowRun, included } =
      await SessionClientDashboard.postResourceSingletonActionNew<
        WorkflowRunSimulated,
        {
          type: ApiModel.WorkflowRunSimulated;
          attributes: {
            'workflow-version-id': string;
            'simulate-context'?: WorkflowRunSimulateContext;
          };
        }
      >(ApiEndpoint.WorkflowRun, 'create-simulated', {
        attributes,
        type: ApiModel.WorkflowRunSimulated,
      });
    dispatch(setWorkflowRun(workflowRun));
    //simulateContext is only populated when user wants to restart a simulated run
    if (simulateContext) {
      dispatch(runSimulatedWorkflowRun(workflowRun, navigate));
    }
    if (included) {
      dispatch(setWorkflowRunIncluded(workflowRun, included));
    }
    navigate(`/dashboard/workflows/${workflowId}/runs/${workflowRun.id}`);
  };
};

const getRunSteps = (
  workflowRun: WorkflowRun | WorkflowRunSimulated,
  included: ApiResource[],
): WorkflowRunStep[] => {
  const relationshipMap = keyBy('id', included);
  return workflowRun.relationships.steps.data.map(
    ({ id }) => relationshipMap[id],
  ) as WorkflowRunStep[];
};

const getKnownObjectsForRun = (
  steps: WorkflowRunStep[],
  relationshipMap: Dictionary<ApiResource>,
): WorkflowRunStepKnownObjects[] => {
  const allKnownObjects = steps
    .flatMap((step) => step.relationships['known-objects'].data)
    .map((model) => relationshipMap[model.id] as WorkflowRunStepKnownObjects);
  return uniqBy('id', allKnownObjects);
};

export const setWorkflowRunIncluded = (
  workflowRun: WorkflowRun | WorkflowRunSimulated,
  included: ApiResource[],
): DashboardThunkAction => {
  return async (dispatch) => {
    const relationshipMap = keyBy('id', included);
    const steps = getRunSteps(workflowRun, included);

    const triggerObjectId = workflowRun.relationships['trigger-object']?.data?.id;
    const childWorkflowRunIds = workflowRun.relationships['child-workflow-runs'].data.map(
      ({ id }) => id,
    );
    const childWorkflowRuns = await Promise.all(
      childWorkflowRunIds.map((workflowRunId) =>
        SessionClientDashboard.getResource<WorkflowRun>(ApiEndpoint.WorkflowRun, workflowRunId),
      ),
    );

    if (childWorkflowRuns.length) {
      const moduleRunStepsByStepConfigToken = childWorkflowRuns.reduce<
        Record<string, WorkflowRunStep[]>
      >((accum, { data: childRun, included: includedInChildRun = [] }) => {
        const parentStepId = childRun.relationships['parent-workflow-step']?.data.id;
        const moduleConfigStepToken =
          parentStepId &&
          includedInChildRun.find((i): i is WorkflowRunStep => i.id === parentStepId)?.attributes[
            'config-step-token'
          ];

        return {
          ...accum,
          ...(moduleConfigStepToken
            ? { [moduleConfigStepToken]: getRunSteps(childRun, includedInChildRun) }
            : {}),
        };
      }, {});
      dispatch(setModuleRunStepsByConfigStepToken(moduleRunStepsByStepConfigToken));
    }

    dispatch(setWorkflowRunSteps(steps));
    if (triggerObjectId) {
      dispatch(setWorkflowRunTriggerObject(relationshipMap[triggerObjectId] as TriggerObject));
    }

    const allKnownObjects = [
      ...getKnownObjectsForRun(steps, relationshipMap),
      ...childWorkflowRuns.flatMap(({ data: childRun, included: childRunIncluded = [] }) => {
        const relationshipMap = keyBy('id', childRunIncluded);
        const steps = getRunSteps(childRun, childRunIncluded);
        return getKnownObjectsForRun(steps, relationshipMap);
      }),
    ];
    dispatch(setWorkflowRunKnownObjects(allKnownObjects));
    const { data: workflowVersion, included: includedWorkflowVersion = [] } =
      await SessionClientDashboard.getResource<WorkflowVersion>(
        ApiEndpoint.WorkflowVersion,
        workflowRun.relationships['workflow-version'].data.id,
      );

    const trigger = includedWorkflowVersion.find((include: ApiResource): include is Trigger =>
      ALL_TRIGGER_TYPES.some((triggerType) => triggerType === include.type),
    );
    const stepsConfig = await dispatch(setWorkflowEditorContext(workflowVersion, trigger, true));

    dispatch(
      setWorkflowVersion({
        ...workflowVersion,
        attributes: { ...workflowVersion.attributes, 'steps-config': stepsConfig },
      }),
    );

    const { data: workflow } = await SessionClientDashboard.getResource<Workflow>(
      ApiEndpoint.Workflow,
      workflowRun.relationships.workflow.data.id,
    );

    dispatch(setWorkflow(workflow));
  };
};
